<template>
  <div class="plinko-admin-config">
    <!-- Header -->
    <div class="config-header">
      <h2 class="config-title">{{ $t('admin.config.plinko.title') }}</h2>
      <p class="config-subtitle">{{ $t('admin.config.plinko.subtitle') }}</p>
    </div>

    <!-- Tab bar -->
    <div class="tab-bar" role="tablist">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        class="tab-btn"
        :class="{ active: activeTab === tab.id }"
        role="tab"
        :aria-selected="activeTab === tab.id"
        @click="activeTab = tab.id"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Tab: Multiplier Tables -->
    <div v-if="activeTab === 'multipliers'" class="tab-panel">
      <div v-if="multipliersLoading" class="loading-state">{{ $t('admin.config.loading') }}</div>
      <div v-else-if="multipliersError" class="error-banner">{{ multipliersError }}</div>
      <template v-else>
        <!-- Risk selector -->
        <div class="risk-selector">
          <button
            v-for="risk in risks"
            :key="risk"
            class="risk-btn"
            :class="[`risk-${risk}`, { active: selectedRisk === risk }]"
            @click="selectedRisk = risk"
          >
            {{ capitalise(risk) }}
          </button>
        </div>

        <!-- Multiplier table for selected risk -->
        <div v-for="rows in rowCounts" :key="`${selectedRisk}-${rows}`" class="mult-row-card">
          <div class="mult-row-header">
            <span class="row-label">{{ rows }} Rows</span>
            <span class="bucket-label">{{ rows + 1 }} buckets</span>
            <span
              class="house-edge-badge"
              :class="houseEdgeBadgeClass(getHouseEdge(selectedRisk, rows))"
            >
              HE {{ getHouseEdge(selectedRisk, rows).toFixed(2) }}%
            </span>
            <span
              v-if="getConfigSource(selectedRisk, rows) === 'database'"
              class="source-badge source-db"
            >DB</span>
            <span v-else class="source-badge source-default">Default</span>
          </div>

          <!-- Multiplier inputs -->
          <div class="mult-inputs-wrap">
            <div
              v-for="(mult, idx) in editableMultipliers[selectedRisk][rows]"
              :key="idx"
              class="mult-cell"
            >
              <label class="bucket-idx">B{{ idx }}</label>
              <input
                v-model.number="editableMultipliers[selectedRisk][rows][idx]"
                type="number"
                step="0.01"
                min="0"
                class="mult-input"
                :class="multInputClass(mult)"
                @change="recomputeHouseEdge(selectedRisk, rows)"
              />
            </div>
          </div>

          <!-- Actions for this row -->
          <div class="mult-row-actions">
            <button
              class="btn btn-primary btn-sm"
              :disabled="saving"
              @click="saveMultiplierRow(selectedRisk, rows)"
            >
              {{ saving ? $t('admin.config.saving') : $t('admin.config.plinko.btn_save_row') }}
            </button>
            <button
              class="btn btn-ghost btn-sm"
              :disabled="saving"
              @click="resetMultiplierRow(selectedRisk, rows)"
            >
              {{ $t('admin.config.reset_to_defaults') }}
            </button>
          </div>
        </div>

        <!-- Save all for current risk -->
        <div class="mult-bulk-actions">
          <button
            class="btn btn-primary"
            :disabled="saving"
            @click="saveAllForRisk(selectedRisk)"
          >
            {{ saving ? $t('admin.config.saving') : $t('admin.config.plinko.btn_save_all_risk', { risk: capitalise(selectedRisk) }) }}
          </button>
          <button
            class="btn btn-ghost"
            :disabled="saving"
            @click="resetAllForRisk(selectedRisk)"
          >
            {{ $t('admin.config.plinko.btn_reset_all_risk', { risk: capitalise(selectedRisk) }) }}
          </button>
        </div>

        <div v-if="multipliersSuccess" class="success-banner">{{ multipliersSuccess }}</div>
      </template>
    </div>

    <!-- Tab: Game Settings -->
    <div v-if="activeTab === 'settings'" class="tab-panel">
      <div v-if="settingsLoading" class="loading-state">{{ $t('admin.config.loading_settings') }}</div>
      <div v-else-if="settingsError" class="error-banner">{{ settingsError }}</div>
      <form v-else class="settings-form" @submit.prevent="saveSettings">
        <!-- Bet limits -->
        <section class="settings-section">
          <h3 class="section-title">{{ $t('admin.config.section_bet_limits') }}</h3>
          <div class="form-row">
            <label class="form-label">
              {{ $t('admin.config.label_min_bet') }}
              <input
                v-model.number="settings.min_bet"
                type="number"
                step="0.01"
                min="0.01"
                class="form-input"
                required
              />
            </label>
            <label class="form-label">
              {{ $t('admin.config.label_max_bet') }}
              <input
                v-model.number="settings.max_bet"
                type="number"
                step="0.01"
                min="0.01"
                class="form-input"
                required
              />
            </label>
          </div>
          <div class="form-row">
            <label class="form-label">
              {{ $t('admin.config.label_max_payout') }}
              <input
                v-model.number="settings.max_payout"
                type="number"
                step="0.01"
                min="0"
                class="form-input"
                required
              />
            </label>
          </div>
        </section>

        <!-- Animation settings -->
        <section class="settings-section">
          <h3 class="section-title">{{ $t('admin.config.plinko.section_animation') }}</h3>
          <div class="form-row">
            <label class="form-label">
              {{ $t('admin.config.plinko.label_ball_drop_speed') }}
              <div class="speed-control">
                <input
                  v-model.number="settings.ball_drop_speed_ms"
                  type="range"
                  min="500"
                  max="10000"
                  step="100"
                  class="speed-slider"
                />
                <span class="speed-value">{{ settings.ball_drop_speed_ms }}ms</span>
              </div>
              <div class="speed-hints">
                <span>500ms (Fast)</span>
                <span>10000ms (Slow)</span>
              </div>
            </label>
          </div>
        </section>

        <!-- Auto-bet -->
        <section class="settings-section">
          <h3 class="section-title">{{ $t('admin.config.plinko.section_game_settings') }}</h3>
          <div class="toggle-row">
            <div class="toggle-info">
              <span class="toggle-label">{{ $t('admin.config.plinko.label_auto_bet') }}</span>
              <span class="toggle-desc">{{ $t('admin.config.plinko.hint_auto_bet') }}</span>
            </div>
            <button
              type="button"
              class="toggle-btn"
              :class="{ active: settings.auto_bet_enabled }"
              role="switch"
              :aria-checked="settings.auto_bet_enabled"
              @click="settings.auto_bet_enabled = !settings.auto_bet_enabled"
            >
              <span class="toggle-thumb" />
            </button>
          </div>
        </section>

        <!-- Submit -->
        <div class="settings-actions">
          <button type="submit" class="btn btn-primary" :disabled="settingsSaving">
            {{ settingsSaving ? $t('admin.config.saving') : $t('admin.config.save_settings') }}
          </button>
          <span v-if="settingsSuccess" class="success-inline">{{ settingsSuccess }}</span>
          <span v-if="settingsFormError" class="error-inline">{{ settingsFormError }}</span>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'

// --------------- Types ---------------

interface MultiplierConfig {
  risk: string
  rows: number
  multipliers: number[]
  house_edge: number
  bucket_count: number
  source: 'database' | 'default'
}

interface PlinkoSettings {
  min_bet: number
  max_bet: number
  max_payout: number
  ball_drop_speed_ms: number
  auto_bet_enabled: boolean
}

// --------------- Constants ---------------

const risks = ['low', 'medium', 'high'] as const
const rowCounts = [8, 12, 14, 16] as const

const DEFAULT_MULTIPLIERS: Record<string, Record<number, number[]>> = {
  low: {
    8: [5.6, 2.1, 1.1, 1.0, 0.5, 1.0, 1.1, 2.1, 5.6],
    12: [10.0, 3.0, 1.6, 1.4, 1.1, 1.0, 0.5, 1.0, 1.1, 1.4, 1.6, 3.0, 10.0],
    14: [7.1, 4.0, 1.9, 1.4, 1.3, 1.1, 1.0, 0.5, 1.0, 1.1, 1.3, 1.4, 1.9, 4.0, 7.1],
    16: [16.0, 9.0, 2.0, 1.4, 1.4, 1.2, 1.1, 1.0, 0.5, 1.0, 1.1, 1.2, 1.4, 1.4, 2.0, 9.0, 16.0],
  },
  medium: {
    8: [13.0, 3.0, 1.3, 0.7, 0.4, 0.7, 1.3, 3.0, 13.0],
    12: [33.0, 11.0, 4.0, 2.0, 1.1, 0.6, 0.3, 0.6, 1.1, 2.0, 4.0, 11.0, 33.0],
    14: [559.0, 30.0, 20.0, 3.0, 1.3, 0.6, 0.4, 0.3, 0.4, 0.6, 1.3, 3.0, 20.0, 30.0, 559.0],
    16: [110.0, 41.0, 10.0, 5.0, 3.0, 1.5, 1.0, 0.5, 0.3, 0.5, 1.0, 1.5, 3.0, 5.0, 10.0, 41.0, 110.0],
  },
  high: {
    8: [29.0, 4.0, 1.5, 0.3, 0.2, 0.3, 1.5, 4.0, 29.0],
    12: [170.0, 24.0, 8.1, 2.0, 0.7, 0.2, 0.2, 0.2, 0.7, 2.0, 8.1, 24.0, 170.0],
    14: [839.0, 50.0, 20.0, 4.4, 1.4, 0.4, 0.2, 0.2, 0.2, 0.4, 1.4, 4.4, 20.0, 50.0, 839.0],
    16: [1000.0, 130.0, 26.0, 9.0, 4.0, 2.0, 0.2, 0.2, 0.2, 0.2, 0.2, 2.0, 4.0, 9.0, 26.0, 130.0, 1000.0],
  },
}

const { t } = useI18n()

// --------------- Tabs ---------------

type TabId = 'multipliers' | 'settings'

const tabs = [
  { id: 'multipliers' as TabId, label: t('admin.config.plinko.tab_multipliers') },
  { id: 'settings' as TabId, label: t('admin.config.plinko.tab_settings') },
]
const activeTab = ref<TabId>('multipliers')

// --------------- Multiplier state ---------------

const selectedRisk = ref<'low' | 'medium' | 'high'>('low')

// Flat list from API
const configMap = ref<Map<string, MultiplierConfig>>(new Map())

// Editable copy, keyed by risk → rows → array
const editableMultipliers = reactive<Record<string, Record<number, number[]>>>({
  low: { 8: [], 12: [], 14: [], 16: [] },
  medium: { 8: [], 12: [], 14: [], 16: [] },
  high: { 8: [], 12: [], 14: [], 16: [] },
})

// Live house edge cache (recomputed on input change)
const liveHouseEdge = reactive<Record<string, Record<number, number>>>({
  low: { 8: 0, 12: 0, 14: 0, 16: 0 },
  medium: { 8: 0, 12: 0, 14: 0, 16: 0 },
  high: { 8: 0, 12: 0, 14: 0, 16: 0 },
})

const multipliersLoading = ref(false)
const multipliersError = ref('')
const multipliersSuccess = ref('')
const saving = ref(false)

// --------------- Settings state ---------------

const settings = reactive<PlinkoSettings>({
  min_bet: 1.0,
  max_bet: 10000.0,
  max_payout: 0,
  ball_drop_speed_ms: 3000,
  auto_bet_enabled: true,
})

const settingsLoading = ref(false)
const settingsError = ref('')
const settingsSuccess = ref('')
const settingsFormError = ref('')
const settingsSaving = ref(false)

// --------------- Helpers ---------------

function capitalise(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function configKey(risk: string, rows: number) {
  return `${risk}:${rows}`
}

function getHouseEdge(risk: string, rows: number): number {
  return liveHouseEdge[risk]?.[rows] ?? 0
}

function getConfigSource(risk: string, rows: number): 'database' | 'default' {
  return configMap.value.get(configKey(risk, rows))?.source ?? 'default'
}

function houseEdgeBadgeClass(he: number): string {
  if (he <= 1.5) return 'he-green'
  if (he <= 3.0) return 'he-yellow'
  return 'he-red'
}

function multInputClass(val: number): string {
  if (val >= 10) return 'mult-high'
  if (val < 1) return 'mult-low'
  return ''
}

/** Binomial coefficient C(n, k). */
function binomialCoeff(n: number, k: number): number {
  if (k < 0 || k > n) return 0
  let result = 1
  for (let i = 0; i < Math.min(k, n - k); i++) {
    result = (result * (n - i)) / (i + 1)
  }
  return result
}

/** Calculate house edge from multiplier array and row count. */
function calculateHouseEdge(multipliers: number[], rows: number): number {
  const n = rows
  const total = Math.pow(2, n)
  let ev = 0
  for (let k = 0; k <= n; k++) {
    const prob = binomialCoeff(n, k) / total
    ev += prob * (multipliers[k] ?? 0)
  }
  return (1 - ev) * 100
}

function recomputeHouseEdge(risk: string, rows: number) {
  const mults = editableMultipliers[risk]?.[rows]
  if (mults) {
    liveHouseEdge[risk][rows] = calculateHouseEdge(mults, rows)
  }
}

// --------------- Data loading ---------------

async function loadMultiplierConfigs() {
  multipliersLoading.value = true
  multipliersError.value = ''
  try {
    const res = await apiClient.get<{ configs: MultiplierConfig[] }>('/admin/api/games/plinko/config')
    const configs = res.data.configs
    configMap.value.clear()
    for (const cfg of configs) {
      configMap.value.set(configKey(cfg.risk, cfg.rows), cfg)
      editableMultipliers[cfg.risk][cfg.rows] = [...cfg.multipliers]
      liveHouseEdge[cfg.risk][cfg.rows] = cfg.house_edge
    }
  } catch {
    multipliersError.value = t('admin.config.plinko.error_load_multipliers')
  } finally {
    multipliersLoading.value = false
  }
}

async function loadSettings() {
  settingsLoading.value = true
  settingsError.value = ''
  try {
    const res = await apiClient.get<{
      min_bet: string
      max_bet: string
      max_payout: string
      ball_drop_speed_ms: number
      auto_bet_enabled: boolean
    }>('/admin/api/games/plinko/settings')
    const d = res.data
    settings.min_bet = parseFloat(d.min_bet)
    settings.max_bet = parseFloat(d.max_bet)
    settings.max_payout = parseFloat(d.max_payout)
    settings.ball_drop_speed_ms = d.ball_drop_speed_ms
    settings.auto_bet_enabled = d.auto_bet_enabled
  } catch {
    settingsError.value = t('admin.config.plinko.error_load_settings')
  } finally {
    settingsLoading.value = false
  }
}

// --------------- Actions ---------------

async function saveMultiplierRow(risk: string, rows: number) {
  saving.value = true
  multipliersSuccess.value = ''
  multipliersError.value = ''
  try {
    await apiClient.post('/admin/api/games/plinko/config', {
      configs: [{ risk, rows, multipliers: editableMultipliers[risk][rows] }],
    })
    recomputeHouseEdge(risk, rows)
    // Update source in configMap
    const key = configKey(risk, rows)
    const existing = configMap.value.get(key)
    if (existing) {
      existing.source = 'database'
      existing.house_edge = liveHouseEdge[risk][rows]
    }
    multipliersSuccess.value = t('admin.config.plinko.mult_save_success')
    setTimeout(() => {
      multipliersSuccess.value = ''
    }, 3000)
  } catch {
    multipliersError.value = t('admin.config.plinko.mult_error_save')
  } finally {
    saving.value = false
  }
}

async function resetMultiplierRow(risk: string, rows: number) {
  saving.value = true
  multipliersError.value = ''
  try {
    await apiClient.post('/admin/api/games/plinko/config/reset', { risk, rows })
    // Restore to defaults
    editableMultipliers[risk][rows] = [...DEFAULT_MULTIPLIERS[risk][rows]]
    recomputeHouseEdge(risk, rows)
    const key = configKey(risk, rows)
    const existing = configMap.value.get(key)
    if (existing) existing.source = 'default'
    multipliersSuccess.value = t('admin.config.plinko.mult_save_success')
    setTimeout(() => {
      multipliersSuccess.value = ''
    }, 3000)
  } catch {
    multipliersError.value = t('admin.config.plinko.mult_error_save')
  } finally {
    saving.value = false
  }
}

async function saveAllForRisk(risk: string) {
  saving.value = true
  multipliersSuccess.value = ''
  multipliersError.value = ''
  try {
    const configs = rowCounts.map((rows) => ({
      risk,
      rows,
      multipliers: editableMultipliers[risk][rows],
    }))
    await apiClient.post('/admin/api/games/plinko/config', { configs })
    for (const rows of rowCounts) {
      recomputeHouseEdge(risk, rows)
      const key = configKey(risk, rows)
      const existing = configMap.value.get(key)
      if (existing) {
        existing.source = 'database'
        existing.house_edge = liveHouseEdge[risk][rows]
      }
    }
    multipliersSuccess.value = t('admin.config.plinko.mult_save_success')
    setTimeout(() => {
      multipliersSuccess.value = ''
    }, 3000)
  } catch {
    multipliersError.value = t('admin.config.plinko.mult_error_save')
  } finally {
    saving.value = false
  }
}

async function resetAllForRisk(risk: string) {
  saving.value = true
  multipliersError.value = ''
  try {
    await apiClient.post('/admin/api/games/plinko/config/reset', { risk })
    for (const rows of rowCounts) {
      editableMultipliers[risk][rows] = [...DEFAULT_MULTIPLIERS[risk][rows]]
      recomputeHouseEdge(risk, rows)
      const key = configKey(risk, rows)
      const existing = configMap.value.get(key)
      if (existing) existing.source = 'default'
    }
    multipliersSuccess.value = t('admin.config.plinko.mult_save_success')
    setTimeout(() => {
      multipliersSuccess.value = ''
    }, 3000)
  } catch {
    multipliersError.value = t('admin.config.plinko.mult_error_save')
  } finally {
    saving.value = false
  }
}

async function saveSettings() {
  settingsSaving.value = true
  settingsSuccess.value = ''
  settingsFormError.value = ''
  try {
    await apiClient.post('/admin/api/games/plinko/settings', {
      min_bet: settings.min_bet,
      max_bet: settings.max_bet,
      max_payout: settings.max_payout,
      ball_drop_speed_ms: settings.ball_drop_speed_ms,
      auto_bet_enabled: settings.auto_bet_enabled,
    })
    settingsSuccess.value = t('admin.config.plinko.settings_save_success')
    setTimeout(() => {
      settingsSuccess.value = ''
    }, 3000)
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } }
    settingsFormError.value = e?.response?.data?.message ?? t('admin.config.plinko.settings_error_save')
  } finally {
    settingsSaving.value = false
  }
}

// --------------- Mount ---------------

onMounted(() => {
  void loadMultiplierConfigs()
  void loadSettings()
})
</script>

<style scoped>
.plinko-admin-config {
  max-width: 960px;
}

/* Header */
.config-header {
  margin-bottom: 20px;
}
.config-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}
.config-subtitle {
  font-size: 0.875rem;
  color: var(--text-secondary, #b1bad3);
  margin: 0;
}

/* Tabs */
.tab-bar {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid var(--border, #2d4a5a);
  margin-bottom: 24px;
}
.tab-btn {
  padding: 10px 20px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--text-secondary, #b1bad3);
  font-size: 0.9rem;
  cursor: pointer;
  margin-bottom: -1px;
  transition: color 0.15s, border-color 0.15s;
}
.tab-btn.active {
  color: var(--accent, #1a9e6e);
  border-bottom-color: var(--accent, #1a9e6e);
}
.tab-btn:hover:not(.active) {
  color: var(--text-primary, #fff);
}

/* Tab panels */
.tab-panel {
  animation: fadeIn 0.2s ease;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

/* States */
.loading-state {
  padding: 40px;
  text-align: center;
  color: var(--text-secondary, #b1bad3);
}
.error-banner {
  padding: 12px 16px;
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 8px;
  color: var(--color-danger-faint, #fca5a5);
  margin-bottom: 16px;
}
.success-banner {
  padding: 12px 16px;
  background: rgba(26, 158, 110, 0.12);
  border: 1px solid rgba(26, 158, 110, 0.4);
  border-radius: 8px;
  color: var(--color-success-faint, #6ee7b7);
  margin-top: 16px;
}

/* Risk selector */
.risk-selector {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
}
.risk-btn {
  padding: 8px 20px;
  border-radius: 6px;
  border: 1px solid var(--border, #2d4a5a);
  background: var(--bg-secondary, #213743);
  color: var(--text-secondary, #b1bad3);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.15s;
}
.risk-btn.active,
.risk-btn:hover {
  color: var(--color-on-dark, #fff);
}
.risk-btn.risk-low.active { background: var(--color-success-bg, #1a4a3a); border-color: var(--color-success-mid, #1a9e6e); color: var(--color-success-faint, #6ee7b7); }
.risk-btn.risk-medium.active { background: var(--color-warning-bg, #4a3a10); border-color: var(--color-warning, #d97706); color: var(--color-warning-light, #fbbf24); }
.risk-btn.risk-high.active { background: var(--color-danger-bg, #4a1a1a); border-color: var(--color-danger, #dc2626); color: var(--color-danger-faint, #fca5a5); }

/* Multiplier row cards */
.mult-row-card {
  background: var(--bg-secondary, #213743);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
}
.mult-row-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.row-label {
  font-weight: 600;
  color: var(--text-primary, #fff);
  font-size: 0.95rem;
}
.bucket-label {
  font-size: 0.8rem;
  color: var(--text-secondary, #b1bad3);
}
.house-edge-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  margin-left: auto;
}
.he-green { background: var(--color-success-bg, rgba(26, 158, 110, 0.15)); color: var(--color-success-faint, #6ee7b7); border: 1px solid var(--color-success-border, rgba(26, 158, 110, 0.4)); }
.he-yellow { background: var(--color-warning-bg, rgba(217, 119, 6, 0.15)); color: var(--color-warning-light, #fbbf24); border: 1px solid var(--color-warning-border, rgba(217, 119, 6, 0.4)); }
.he-red { background: var(--color-danger-bg, rgba(220, 38, 38, 0.15)); color: var(--color-danger-faint, #fca5a5); border: 1px solid var(--color-danger-border, rgba(220, 38, 38, 0.4)); }
.source-badge {
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
}
.source-db { background: var(--color-info-bg, rgba(59, 130, 246, 0.2)); color: var(--color-info-faint, #93c5fd); }
.source-default { background: rgba(107, 114, 128, 0.2); color: var(--color-muted, #9ca3af); }

.mult-inputs-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}
.mult-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}
.bucket-idx {
  font-size: 0.65rem;
  color: var(--text-secondary, #b1bad3);
}
.mult-input {
  width: 70px;
  padding: 6px 6px;
  background: var(--bg-primary, #0f1923);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 6px;
  color: var(--text-primary, #fff);
  font-size: 0.8rem;
  text-align: center;
  transition: border-color 0.15s;
}
.mult-input:focus {
  outline: none;
  border-color: var(--accent, #1a9e6e);
}
.mult-input.mult-high { border-color: rgba(26, 158, 110, 0.5); color: var(--color-success-faint, #6ee7b7); }
.mult-input.mult-low { border-color: rgba(220, 38, 38, 0.5); color: var(--color-danger-faint, #fca5a5); }

.mult-row-actions {
  display: flex;
  gap: 8px;
}
.mult-bulk-actions {
  display: flex;
  gap: 10px;
  margin-top: 8px;
  padding-top: 20px;
  border-top: 1px solid var(--border, #2d4a5a);
}

/* Settings form */
.settings-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.settings-section {
  background: var(--bg-secondary, #213743);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 10px;
  padding: 20px;
}
.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-secondary, #b1bad3);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 16px;
}
.form-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}
.form-label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.875rem;
  color: var(--text-secondary, #b1bad3);
  flex: 1;
  min-width: 180px;
}
.form-input {
  padding: 10px 12px;
  background: var(--bg-primary, #0f1923);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 8px;
  color: var(--text-primary, #fff);
  font-size: 0.9rem;
  transition: border-color 0.15s;
}
.form-input:focus {
  outline: none;
  border-color: var(--accent, #1a9e6e);
}

/* Speed slider */
.speed-control {
  display: flex;
  align-items: center;
  gap: 12px;
}
.speed-slider {
  flex: 1;
  accent-color: var(--accent, #1a9e6e);
  cursor: pointer;
}
.speed-value {
  min-width: 60px;
  text-align: right;
  font-size: 0.875rem;
  color: var(--text-primary, #fff);
  font-weight: 600;
}
.speed-hints {
  display: flex;
  justify-content: space-between;
  font-size: 0.7rem;
  color: var(--text-secondary, #b1bad3);
}

/* Auto-bet toggle */
.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.toggle-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.toggle-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-primary, #fff);
}
.toggle-desc {
  font-size: 0.8rem;
  color: var(--text-secondary, #b1bad3);
}
.toggle-btn {
  position: relative;
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background: var(--bg-primary, #0f1923);
  border: 1px solid var(--border, #2d4a5a);
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
  flex-shrink: 0;
}
.toggle-btn.active {
  background: var(--accent, #1a9e6e);
  border-color: var(--accent, #1a9e6e);
}
.toggle-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--color-on-dark, #fff);
  transition: transform 0.2s;
}
.toggle-btn.active .toggle-thumb {
  transform: translateX(20px);
}

/* Settings actions */
.settings-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}
.success-inline { font-size: 0.875rem; color: var(--color-success-faint, #6ee7b7); }
.error-inline { font-size: 0.875rem; color: var(--color-danger-faint, #fca5a5); }

/* Buttons */
.btn {
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s, background 0.15s;
}
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.btn-primary {
  background: var(--accent, #1a9e6e);
  color: var(--color-on-dark, #fff);
}
.btn-primary:hover:not(:disabled) {
  background: var(--accent-primary-hover, #1abc7e);
}
.btn-ghost {
  background: transparent;
  color: var(--text-secondary, #b1bad3);
  border: 1px solid var(--border, #2d4a5a);
}
.btn-ghost:hover:not(:disabled) {
  color: var(--text-primary, #fff);
  border-color: var(--text-secondary, #b1bad3);
}
.btn-sm {
  padding: 6px 14px;
  font-size: 0.8rem;
}

/* Number inputs: remove arrows */
input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
}
input[type='number'] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
